import { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Form, Badge } from "react-bootstrap";
import { listFieldEntries, getPlantCatalogue, reviewCompetitorEntry } from "../../services/fieldRatesService";
import { confirmVendorRates, rejectVendorRates } from "../../services/vendorInboxService";
import { listVendors } from "../../services/catalogueService";
import { errorToast, successToast } from "../Toasts/Toasts";

// Every rate staff keyed into the Nursery Bhav app, in one table.
//
// The vendor inbox shows the same entries a nursery at a time; this is the
// across-the-board view, newest first, filterable by who, where and when.
// Confirm and Reject are the inbox's own actions, so a rate is logged one
// way whichever page it is confirmed on.
//
// A Draft is an entry whose plant staff have not named yet - it waits for
// them, not for MG. An entry from a nursery not linked to Zoho needs a
// vendor picked before it can be logged.
//
// Competitors (local nurseries) are a second view: what they sell at, beside
// what we sell at on Shopify. Those are looked at and marked Reviewed -
// never logged to Zoho, which holds our costs.

const TABS = {
  supplier: [["Pending", "To confirm"], ["Draft", "Plant not named"], ["Logged", "Logged"], ["Rejected", "Rejected"], ["", "All"]],
  competitor: [["ToReview", "To review"], ["Draft", "Plant not named"], ["Reviewed", "Reviewed"], ["Rejected", "Rejected"], ["", "All"]],
};
const BADGE = { Pending: "warning", ToReview: "warning", Draft: "secondary", Logged: "success", Answered: "success", Reviewed: "success", Rejected: "danger" };
const LABEL = { Pending: "To confirm", ToReview: "To review", Answered: "Superseded" };
const when = (d) => new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const size = (r) => [r.height, r.bagSize].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(", ");

const FieldRates = () => {
  const [kind, setKind] = useState("supplier");
  const [tab, setTab] = useState("Pending");
  const [staffId, setStaffId] = useState("");
  const [nursery, setNursery] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [plants, setPlants] = useState([]);
  const [vendors, setVendors] = useState([]);

  const load = useCallback(async () => {
    try {
      setData(await listFieldEntries({ kind, status: tab || undefined, staffId: staffId || undefined, nursery: nursery || undefined, from: from || undefined, to: to || undefined }));
    } catch (e) {
      errorToast("Could not load field rates");
    }
  }, [kind, tab, staffId, nursery, from, to]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getPlantCatalogue().then(setPlants).catch(() => {});
    listVendors().then((v) => setVendors(v.filter((x) => x.name !== "Unidentified"))).catch(() => {});
  }, []);

  if (!data) return null;

  return (
    <div className="page-body px-0 py-lg-2 py-1 mt-0 mt-lg-3 ms-3">
      <div className="container-fluid p-0">
        <div className="row-title px-2 d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <h6 className="fw-bold mb-0">Field rates</h6>
            <div className="small text-muted">Rates staff entered in the Nursery Bhav app. Confirming logs the rate to Zoho as "Field visit - name".</div>
          </div>
          <Button size="sm" variant="outline-secondary" onClick={load}>Refresh</Button>
        </div>

        <div className="px-2 mt-2 d-flex flex-wrap gap-2 align-items-center">
          <div className="btn-group btn-group-sm me-2" role="group">
            {[["supplier", "Suppliers"], ["competitor", "Competitors"]].map(([k, label]) => (
              <button key={k} type="button" className={`btn ${kind === k ? "btn-dark" : "btn-outline-dark"}`}
                onClick={() => { setKind(k); setTab(k === "competitor" ? "ToReview" : "Pending"); }}>
                {label}
              </button>
            ))}
          </div>
          <div className="btn-group btn-group-sm" role="group">
            {TABS[kind].map(([k, label]) => (
              <button key={k || "all"} type="button" className={`btn ${tab === k ? "btn-success" : "btn-outline-success"}`} onClick={() => setTab(k)}>
                {label}{k && data.counts[k] !== undefined ? ` (${data.counts[k]})` : ""}
              </button>
            ))}
          </div>
          <Form.Select size="sm" style={{ width: 170 }} value={staffId} onChange={(e) => setStaffId(e.target.value)}>
            <option value="">All staff</option>
            {data.staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Form.Select>
          <Form.Control size="sm" style={{ width: 190 }} placeholder="Nursery contains…" value={nursery} onChange={(e) => setNursery(e.target.value)} />
          <Form.Control size="sm" type="date" style={{ width: 150 }} value={from} onChange={(e) => setFrom(e.target.value)} title="From" />
          <Form.Control size="sm" type="date" style={{ width: 150 }} value={to} onChange={(e) => setTo(e.target.value)} title="To" />
        </div>

        <div className="px-2 mt-2">
          {data.rows.length === 0 ? (
            <div className="text-muted small py-4">Nothing here for these filters.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th style={{ width: 110 }}>Photos</th>
                    <th>When · who</th>
                    <th>Nursery</th>
                    <th>What staff entered</th>
                    <th style={{ minWidth: 380 }}>{kind === "competitor" ? "Our price" : "Log as"}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => <Row key={r.id} r={r} plants={plants} vendors={vendors} reload={load} />)}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function Row({ r, plants, vendors, reload }) {
  const b = r.binding || {};
  const startProduct = b.productId || r.productId || "";
  const [productId, setProductId] = useState(startProduct);
  const [variantId, setVariantId] = useState(b.status === "bound" ? b.variantId : "");
  const [rate, setRate] = useState(r.rate === null || r.rate === undefined ? "" : String(r.rate));
  const [vendorId, setVendorId] = useState("");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);

  const product = useMemo(() => plants.find((p) => p.id === productId), [plants, productId]);
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return plants.filter((p) => p.name.toLowerCase().includes(q) || (p.scientificName || "").toLowerCase().includes(q)).slice(0, 8);
  }, [plants, query]);

  const act = async (fn, done) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res && res.success === false) errorToast(res.message);
      else { successToast(done); await reload(); }
    } catch (e) {
      errorToast(e?.response?.data?.message || e?.message);
    } finally {
      setBusy(false);
    }
  };

  const confirm = () => {
    if (!variantId) return errorToast("Pick the plant and size to log it as");
    if (!(Number(rate) > 0)) return errorToast("Enter the rate");
    if (r.unidentified && !vendorId) return errorToast("This nursery is not linked to Zoho: pick the vendor to log it under");
    const payload = { lines: [{ variantId, rate: Number(rate), plantName: r.plantName, height: r.height, bagSize: r.bagSize }] };
    if (r.unidentified) payload.vendorId = vendorId;
    act(() => confirmVendorRates(r.id, payload), "Logged to Zoho");
  };

  const open = r.status === "Pending";
  return (
    <tr>
      <td>
        <div className="d-flex flex-wrap gap-1">
          {(r.photoUrls || []).map((u, i) => (
            <a key={i} href={u} target="_blank" rel="noreferrer">
              <img src={u} alt="" style={{ width: i === 0 ? 96 : 44, height: i === 0 ? 96 : 44, objectFit: "cover", borderRadius: 4 }} />
            </a>
          ))}
        </div>
      </td>
      <td className="small">
        <div>{when(r.at)}</div>
        <div className="text-muted">{r.staff || "—"}</div>
      </td>
      <td className="small">
        <div>{r.nursery}</div>
        {r.nurseryKind === "competitor" && <Badge bg="dark">competitor</Badge>}
        {r.unidentified && <Badge bg="light" text="dark">not linked to Zoho</Badge>}
        {r.typedAs && <div className="text-muted">entered as {r.typedAs}</div>}
      </td>
      <td className="small">
        <div className="fw-semibold">{r.plantName || <span className="text-muted">Plant not named</span>}</div>
        {size(r) && <div className="text-muted">{size(r)}</div>}
        <div>₹{r.rate}</div>
        {r.note && <div className="text-muted fst-italic">{r.note}</div>}
        <Badge bg={BADGE[r.status] || "secondary"} className="mt-1">{LABEL[r.status] || r.status}</Badge>
      </td>
      {r.nurseryKind === "competitor" ? (
        <td className="small">
          {(r.ourPrices || []).length > 0 ? (
            <ul className="mb-1 ps-3">
              {r.ourPrices.map((o) => <li key={o.name}>{o.name} — ₹{o.price} <span className="text-muted">on Shopify</span></li>)}
            </ul>
          ) : (
            <div className="text-muted mb-1">{r.plantName ? "We don't sell this plant on Shopify." : "Plant not named yet."}</div>
          )}
          {r.status === "ToReview" && (
            <div className="d-flex gap-1">
              <Button size="sm" variant="success" disabled={busy} onClick={() => act(() => reviewCompetitorEntry(r.id, "Reviewed"), "Marked reviewed")}>Reviewed</Button>
              <Button size="sm" variant="outline-danger" disabled={busy} onClick={() => act(() => reviewCompetitorEntry(r.id, "Rejected"), "Rejected")}>Reject</Button>
            </div>
          )}
          {r.status === "Draft" && <span className="text-muted">Waiting for {r.staff || "staff"} to name the plant in the app.</span>}
        </td>
      ) : (
      <td className="small">
        {r.status === "Draft" && <span className="text-muted">Waiting for {r.staff || "staff"} to name the plant in the app.</span>}
        {(r.status === "Logged" || r.status === "Answered") && (
          <ul className="mb-0 ps-3">
            {(r.loggedRates || []).map((l) => <li key={l.rateId}>{l.name} — ₹{l.rate}</li>)}
            {!(r.loggedRates || []).length && <li className="text-muted">Covered by a later confirmed rate</li>}
          </ul>
        )}
        {r.status === "Rejected" && <span className="text-muted">{r.error || "Rejected"}</span>}
        {open && (
          <div className="d-flex flex-column gap-1">
            {product ? (
              <div className="d-flex gap-1 align-items-center">
                <span className="fw-semibold">{product.name}</span>
                <Button size="sm" variant="link" className="p-0 ms-1" onClick={() => { setProductId(""); setVariantId(""); }}>change</Button>
              </div>
            ) : (
              <div style={{ position: "relative" }}>
                <Form.Control size="sm" placeholder="Search plant…" value={query} onChange={(e) => setQuery(e.target.value)} />
                {matches.length > 0 && (
                  <div className="border bg-white shadow-sm" style={{ position: "absolute", zIndex: 5, width: "100%" }}>
                    {matches.map((p) => (
                      <div key={p.id} className="px-2 py-1" style={{ cursor: "pointer" }} onClick={() => { setProductId(p.id); setVariantId(""); setQuery(""); }}>
                        {p.name} <span className="text-muted">{p.category || ""}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {product && (
              <Form.Select size="sm" value={variantId} onChange={(e) => setVariantId(e.target.value)}>
                <option value="">— pick the size —</option>
                {product.variants.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
              </Form.Select>
            )}
            {product && product.variants.length === 0 && <span className="text-danger">This plant has no sizes in Zoho yet — add one on its Catalogue page.</span>}
            {r.unidentified && (
              <Form.Select size="sm" value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
                <option value="">— log under which Zoho vendor —</option>
                {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </Form.Select>
            )}
            <div className="d-flex gap-1 align-items-center">
              <span>₹</span>
              <Form.Control size="sm" type="number" style={{ width: 100 }} value={rate} onChange={(e) => setRate(e.target.value)} />
              <Button size="sm" variant="success" disabled={busy} onClick={confirm}>Confirm &amp; log</Button>
              <Button size="sm" variant="outline-danger" disabled={busy} onClick={() => act(() => rejectVendorRates(r.id, "rejected on Field rates"), "Rejected")}>Reject</Button>
            </div>
          </div>
        )}
      </td>
      )}
    </tr>
  );
}

export default FieldRates;
